import React, { useState, useEffect } from 'react';
import { getRandomWord } from '../../utils/stringUtils';
import { apiClient } from '../../api';

interface PublishMpxStepProps {
  publishConfig: {
    connectionPhrase: string;
    headline: string;
    description: string;
    expiryDate: string | null;
    noExpiry: boolean;
    limitUses: number | null;
    noLimit: boolean;
  } | null;
  tenantId: string; // MPX tenant ID
  onConfigChange: (config: any) => void;
  onBack: () => void;
}

const PublishMpxStep: React.FC<PublishMpxStepProps> = ({
  publishConfig,
  tenantId,
  onConfigChange,
  onBack,
}) => {
  const [connectionPhrase, setConnectionPhrase] = useState(publishConfig?.connectionPhrase || '');
  const [headline, setHeadline] = useState(publishConfig?.headline || '');
  const [description, setDescription] = useState(publishConfig?.description || 'Hey there! Join me on Meeting Place!');
  const [expiryDate, setExpiryDate] = useState(publishConfig?.expiryDate || '');
  const [noExpiry, setNoExpiry] = useState(publishConfig?.noExpiry ?? true);
  const [limitUses, setLimitUses] = useState<number | null>(publishConfig?.limitUses || null);
  const [noLimit, setNoLimit] = useState(publishConfig?.noLimit ?? true);
  
  const [checkingPhrase, setCheckingPhrase] = useState(false);
  const [phraseAvailable, setPhraseAvailable] = useState<boolean | null>(null);
  const [phraseCheckError, setPhraseCheckError] = useState<string | null>(null);

  // Check phrase availability when it changes (debounced)
  useEffect(() => {
    if (!connectionPhrase || !tenantId) {
      setPhraseAvailable(null);
      setPhraseCheckError(null);
      return;
    }

    const timeoutId = setTimeout(async () => {
      await checkPhraseAvailability(connectionPhrase);
    }, 500); // Debounce for 500ms

    return () => clearTimeout(timeoutId);
  }, [connectionPhrase, tenantId]);

  const checkPhraseAvailability = async (phrase: string) => {
    if (!phrase || !tenantId) return;

    setCheckingPhrase(true);
    setPhraseAvailable(null);
    setPhraseCheckError(null);

    try {
      const response = await apiClient.post('/mpx-tenants/check-offer-phrase', {
        tenant_id: tenantId,
        offer_phrase: phrase,
      });

      setPhraseAvailable(!response.data.is_in_use);
    } catch (error: any) {
      console.error('Failed to check phrase availability:', error);
      setPhraseCheckError(error.message || 'Failed to check phrase availability');
      setPhraseAvailable(null);
    } finally {
      setCheckingPhrase(false);
    }
  };

  const generateRandomPhrase = () => {
    const words = [];
    for (let i = 0; i < 6; i++) {
      words.push(getRandomWord());
    }
    const phrase = words.join(' ');
    setConnectionPhrase(phrase);
  };

  const handleNext = () => {
    if (connectionPhrase && headline && description) {
      onConfigChange({
        connectionPhrase,
        headline,
        description,
        expiryDate: noExpiry ? null : expiryDate,
        noExpiry,
        limitUses: noLimit ? null : limitUses,
        noLimit,
      });
    }
  };

  const handleLimitUsesChange = (value: string) => {
    const num = parseInt(value, 10);
    if (!isNaN(num) && num >= 1 && num <= 1000) {
      setLimitUses(num);
    } else if (value === '') {
      setLimitUses(null);
    }
  };

  // Get minimum date for expiry (today)
  const getMinDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  return (
    <div className="card shadow">
      <div className="card-body">
        <div className="publish-mpx-step">
          <h5 className="mb-4">
            <span className="badge badge-warning mr-2">MPX</span>
            Meeting Place Invitation Offer
          </h5>

          <p className="text-muted mb-4">
            Configure the invitation details for this meeting place. This information will be
            used to create an invitation that others can use to connect.
          </p>

          <div className="form-group">
            <label htmlFor="connection-phrase">
              Connection Phrase <span className="text-danger">*</span>
            </label>
            <div className="input-group">
              <input
                type="text"
                className="form-control"
                id="connection-phrase"
                value={connectionPhrase}
                onChange={(e) => setConnectionPhrase(e.target.value)}
                placeholder="e.g., customer-support-2024"
                required
              />
              <div className="input-group-append">
                {checkingPhrase ? (
                  <span className="input-group-text">
                    <i className="fas fa-spinner fa-spin"></i>
                  </span>
                ) : (
                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    onClick={generateRandomPhrase}
                    title="Generate random phrase"
                  >
                    <i className="fas fa-random mr-1"></i>
                    Random
                  </button>
                )}
              </div>
            </div>
            
            {phraseCheckError && (
              <div className="alert alert-danger mt-2 mb-0" role="alert">
                <i className="fas fa-exclamation-triangle mr-2"></i>
                <strong>Error:</strong> {phraseCheckError}
              </div>
            )}
            {phraseAvailable === false && (
              <div className="alert alert-warning mt-2 mb-0" role="alert">
                <i className="fas fa-times-circle mr-2"></i>
                This phrase is already in use. Please choose another.
              </div>
            )}
            {phraseAvailable === true && (
              <div className="alert alert-success mt-2 mb-0" role="alert">
                <i className="fas fa-check-circle mr-2"></i>
                This phrase is available!
              </div>
            )}
            <small className="form-text text-muted">
              A memorable phrase that helps others connect to this channel through Meeting Place
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="headline">
              Headline <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className="form-control"
              id="headline"
              value={headline}
              onChange={(e) => setHeadline(e.target.value)}
              placeholder="e.g., Customer Support Channel"
              required
            />
            <small className="form-text text-muted">
              A short, descriptive title for the invitation
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="description">
              Description <span className="text-danger">*</span>
            </label>
            <input
              className="form-control"
              id="description"              
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Add a description of this invitation to connect..."
              required
            />
            <small className="form-text text-muted">
              Detailed information about the purpose and use of this meeting place
            </small>
          </div>

          <hr className="my-4" />

          <h6 className="mb-3">Invitation Constraints</h6>

          <div className="form-group">
            <div className="custom-control custom-checkbox">
              <input
                type="checkbox"
                className="custom-control-input"
                id="no-expiry"
                checked={noExpiry}
                onChange={(e) => setNoExpiry(e.target.checked)}
              />
              <label className="custom-control-label" htmlFor="no-expiry">
                Does not expire
              </label>
            </div>
            {!noExpiry && (
              <div className="mt-2">
                <label htmlFor="expiry-date">Expiry Date</label>
                <input
                  type="date"
                  className="form-control"
                  id="expiry-date"
                  value={expiryDate}
                  onChange={(e) => setExpiryDate(e.target.value)}
                  min={getMinDate()}
                  required={!noExpiry}
                  style={{ colorScheme: 'light' }}
                />
                <small className="form-text text-muted">
                  Date format follows your browser locale settings
                </small>
              </div>
            )}
          </div>

          <div className="form-group">
            <div className="custom-control custom-checkbox">
              <input
                type="checkbox"
                className="custom-control-input"
                id="no-limit"
                checked={noLimit}
                onChange={(e) => setNoLimit(e.target.checked)}
              />
              <label className="custom-control-label" htmlFor="no-limit">
                No limit on number of uses
              </label>
            </div>
            {!noLimit && (
              <div className="mt-2">
                <label htmlFor="limit-uses">
                  Maximum Number of Uses (1-1000)
                </label>
                <input
                  type="number"
                  className="form-control"
                  id="limit-uses"
                  value={limitUses || ''}
                  onChange={(e) => handleLimitUsesChange(e.target.value)}
                  min="1"
                  max="1000"
                  placeholder="Enter a number between 1 and 1000"
                  required={!noLimit}
                />
                <small className="form-text text-muted">
                  The invitation will be disabled after this many uses
                </small>
              </div>
            )}
          </div>

          <div className="d-flex justify-content-between mt-4">
            <button className="btn btn-secondary" onClick={onBack}>
              <i className="fas fa-arrow-left"></i> Back
            </button>
            <button
              className="btn btn-primary"
              onClick={handleNext}
              disabled={!connectionPhrase || !headline || !description || (!noExpiry && !expiryDate) || (!noLimit && !limitUses)}
            >
              Next <i className="fas fa-arrow-right"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublishMpxStep;
